import React, { useState, useEffect } from "react";
import {
  Modal,
  Message,
  Table,
  Header,
  Segment,
  Icon,
  Dimmer,
  Loader,
  Label,
} from "semantic-ui-react";
import axios from "../../../../../../../apis/dvdlocator";
import { comparePartNumbers } from "../../../../../../formatting/partNumberSort";

export const InventoryCombinationsDisplay = ({ open, close, product }) => {
  const [combinations, setCombinations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [requestError, setRequestError] = useState(null);

  const productId = product.product.product_id;

  useEffect(() => {
    async function getProductInventoryCombinations() {
      setIsLoading(true);
      setRequestError(null);

      try {
        const res = await axios.get(
          `/product/${productId}/inventory-combinations`
        );
        setCombinations(res.data);
      } catch (err) {
        console.error(err);
        setRequestError(
          err.response?.data?.message ||
            err.message ||
            "An error occurred while loading combinations."
        );
      } finally {
        setIsLoading(false);
      }
    }

    getProductInventoryCombinations();
  }, [productId]);

  // Restock status priority (lower number = better)
  const getRestockPriority = (status) => {
    switch (status) {
      case "IS":
        return 1; // In Stock - best
      case "BO":
        return 2; // Backorder - middle
      case "OP":
        return 3; // Out of Print - worst
      default:
        return 4; // Unknown - worst case
    }
  };

  // Get the best restock status for a part (from all its supply units)
  const getBestRestockStatusForPart = (part) => {
    if (!part.supply_units || part.supply_units.length === 0) {
      return "OP"; // No supply units means out of print
    }

    const bestStatus = part.supply_units.reduce((best, unit) => {
      const currentPriority = getRestockPriority(unit.restock_status_ftp);
      const bestPriority = getRestockPriority(best);
      return currentPriority < bestPriority ? unit.restock_status_ftp : best;
    }, "OP");

    return bestStatus;
  };

  // Get overall combination restock status
  const getCombinationRestockStatus = (parts) => {
    const partStatuses = parts.map(getBestRestockStatusForPart);

    // If any part is OP (out of print), the whole combination is OP
    if (partStatuses.includes("OP")) {
      return "OP";
    }

    // If all parts are IS (in stock), the combination is IS
    if (partStatuses.every((status) => status === "IS")) {
      return "IS";
    }

    // Otherwise, the combination is BO (backorder)
    return "BO";
  };

  // Get color for restock status
  const getRestockStatusColor = (status) => {
    switch (status) {
      case "IS":
        return "green";
      case "BO":
        return "yellow";
      case "OP":
        return "red";
      default:
        return "grey";
    }
  };

  // Get readable label for restock status
  const getRestockStatusLabel = (status) => {
    switch (status) {
      case "IS":
        return "In Stock";
      case "BO":
        return "Backorder";
      case "OP":
        return "Out of Print";
      default:
        return "Unknown";
    }
  };

  // Calculate lowest cost and 90-day cost for a combination
  const calculateCosts = (parts) => {
    let hasMissingPrice = false;
    let hasMissing90DayPrice = false;

    const costs = parts.reduce(
      (acc, part) => {
        const validAvcgs = part.inventory
          .map((inv) => inv.avcg)
          .filter((avcg) => avcg != null);

        const valid90DayAvgs = part.inventory
          .map((inv) => inv.last_ninety_day_avg_cogs)
          .filter((avg) => avg != null);

        if (validAvcgs.length === 0) hasMissingPrice = true;
        if (valid90DayAvgs.length === 0) hasMissing90DayPrice = true;

        return {
          total: acc.total + (validAvcgs.length ? Math.min(...validAvcgs) : 0),
          total90Day:
            acc.total90Day +
            (valid90DayAvgs.length ? Math.min(...valid90DayAvgs) : 0),
        };
      },
      { total: 0, total90Day: 0 }
    );

    return {
      ...costs,
      hasMissingPrice,
      hasMissing90DayPrice,
    };
  };

  // Calculate available inventory for a combination
  const calculateAvailableInventory = (parts) => {
    const partInventories = parts.map((part) => {
      const totalInv = part.inventory.reduce(
        (sum, inv) => sum + (inv.inv || 0),
        0
      );
      return { partNumber: part.part_number, inventory: totalInv };
    });

    return Math.min(...partInventories.map((p) => p.inventory));
  };

  // Group inventory for a specific combination by part number
  const groupCombinationInventory = (parts) => {
    return parts.reduce((acc, part) => {
      if (!acc[part.part_number]) {
        acc[part.part_number] = {
          inventory: {},
          supply_units: part.supply_units || [],
        };
      }

      part.inventory.forEach((inv) => {
        if (!acc[part.part_number].inventory[inv.inventory_id]) {
          acc[part.part_number].inventory[inv.inventory_id] = inv;
        }
      });
      return acc;
    }, {});
  };

  return (
    <Modal open={open} onClose={close}>
      <Modal.Header>
        {product?.product?.product_title || "Product Title"}
      </Modal.Header>

      <Modal.Content>
        {isLoading ? (
          <Dimmer active inverted>
            <Loader inverted content="Loading combinations..." />
          </Dimmer>
        ) : requestError ? (
          <Message
            error
            icon="warning sign"
            header="Error Loading Combinations"
            content={requestError}
          />
        ) : combinations.length === 0 ? (
          <Message
            info
            icon="info circle"
            header="No Combinations"
            content="No combinations found for this product."
          />
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            {combinations.map((combination, index) => {
              const costs = calculateCosts(combination.components);
              const availableInventory = calculateAvailableInventory(
                combination.components
              );
              const restockStatus = getCombinationRestockStatus(
                combination.components
              );

              return (
                <Segment key={combination.combination_id}>
                  <div
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "0.5rem",
                      padding: "0.5rem 0",
                    }}
                    onClick={() =>
                      setCombinations((prev) =>
                        prev.map((c, i) =>
                          i === index ? { ...c, active: !c.active } : c
                        )
                      )
                    }
                  >
                    <Icon
                      name={
                        combination.active ? "chevron down" : "chevron right"
                      }
                      size="small"
                      style={{ marginTop: "0.5rem" }}
                    />
                    <div>
                      <Header as="h3" style={{ margin: 0 }}>
                        Combination: {combination.combination_id}
                        <Header.Subheader style={{ marginTop: "0.3rem" }}>
                          Parts:{" "}
                          {combination.components
                            .map((part) => part.part_number)
                            .sort(comparePartNumbers)
                            .join(", ")}
                        </Header.Subheader>
                        <Header.Subheader style={{ marginTop: "0.2rem" }}>
                          Lowest Cost:{" "}
                          {costs.hasMissingPrice
                            ? "Part(s) missing AVCG"
                            : `$${costs.total.toFixed(2)}`}
                        </Header.Subheader>
                        <Header.Subheader style={{ marginTop: "0.2rem" }}>
                          Lowest Cost 90 Day:{" "}
                          {costs.hasMissing90DayPrice
                            ? "Part(s) missing 90-day COGS"
                            : `$${costs.total90Day.toFixed(2)}`}
                        </Header.Subheader>
                        <Header.Subheader style={{ marginTop: "0.2rem" }}>
                          Available Sets: {availableInventory}
                        </Header.Subheader>
                        <Header.Subheader style={{ marginTop: "0.2rem" }}>
                          Restock Status:{" "}
                          <Label
                            color={getRestockStatusColor(restockStatus)}
                            size="tiny"
                            style={{ marginLeft: "0.5rem" }}
                          >
                            {getRestockStatusLabel(restockStatus)}
                          </Label>
                        </Header.Subheader>
                      </Header>
                    </div>
                  </div>

                  {combination.active && (
                    <div style={{ marginTop: "1rem", marginLeft: "1.5rem" }}>
                      <Table basic="very">
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>Part #</Table.HeaderCell>
                            <Table.HeaderCell>Inventory ID</Table.HeaderCell>
                            <Table.HeaderCell>INV</Table.HeaderCell>
                            <Table.HeaderCell>Shelf</Table.HeaderCell>
                            <Table.HeaderCell>AVCG</Table.HeaderCell>
                            <Table.HeaderCell>AVCG 90</Table.HeaderCell>
                            <Table.HeaderCell>Supply Info</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {Object.entries(
                            groupCombinationInventory(combination.components)
                          )
                            .sort(([partNumberA], [partNumberB]) =>
                              comparePartNumbers(partNumberA, partNumberB)
                            )
                            .map(([partNumber, partData], groupIndex) => {
                              const inventories = partData.inventory;
                              const supplyUnits = partData.supply_units;

                              const validAvcgs = Object.values(inventories)
                                .map((inv) => inv.avcg)
                                .filter((avcg) => avcg != null);

                              const valid90DayAvgs = Object.values(inventories)
                                .map((inv) => inv.last_ninety_day_avg_cogs)
                                .filter((avg) => avg != null);

                              const lowestAvcg =
                                validAvcgs.length > 0
                                  ? Math.min(...validAvcgs)
                                  : null;

                              const lowest90DayAvg =
                                valid90DayAvgs.length > 0
                                  ? Math.min(...valid90DayAvgs)
                                  : null;

                              const bestPartRestockStatus =
                                getBestRestockStatusForPart({
                                  supply_units: supplyUnits,
                                });

                              return (
                                <React.Fragment key={partNumber}>
                                  <Table.Row>
                                    <Table.Cell
                                      colSpan="7"
                                      style={{ padding: "0.3em" }}
                                    >
                                      <Header
                                        as="h4"
                                        style={{
                                          margin: "0.2em 0",
                                          color: "rgba(0, 0, 0, 0.7)",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0.5rem",
                                        }}
                                      >
                                        {partNumber}
                                        <Label
                                          color={getRestockStatusColor(
                                            bestPartRestockStatus
                                          )}
                                          size="tiny"
                                        >
                                          {getRestockStatusLabel(
                                            bestPartRestockStatus
                                          )}
                                        </Label>
                                      </Header>
                                    </Table.Cell>
                                  </Table.Row>
                                  {Object.entries(inventories).map(
                                    ([inventoryId, inv], rowIndex) => (
                                      <Table.Row
                                        key={`${partNumber}-${inventoryId}`}
                                        style={{
                                          color: "rgba(0, 0, 0, 0.6)",
                                          backgroundColor:
                                            rowIndex % 2 === 0
                                              ? "rgba(0,0,0,0.02)"
                                              : "transparent",
                                        }}
                                      >
                                        <Table.Cell
                                          style={{ padding: "0.3em" }}
                                        ></Table.Cell>
                                        <Table.Cell
                                          style={{ padding: "0.3em" }}
                                        >
                                          {inventoryId}
                                        </Table.Cell>
                                        <Table.Cell
                                          style={{ padding: "0.3em" }}
                                        >
                                          {inv.inv}
                                        </Table.Cell>
                                        <Table.Cell
                                          style={{ padding: "0.3em" }}
                                        >
                                          {inv.inventory_shelf || "N/A"}
                                        </Table.Cell>
                                        <Table.Cell
                                          style={{ padding: "0.3em" }}
                                        >
                                          <span
                                            style={{
                                              backgroundColor:
                                                inv.avcg === lowestAvcg &&
                                                inv.avcg != null
                                                  ? "#e6ffe6"
                                                  : "transparent",
                                              padding: "0.2rem 0.4rem",
                                              borderRadius: "2px",
                                            }}
                                          >
                                            {inv.avcg != null
                                              ? `${new Intl.NumberFormat(
                                                  "en-US",
                                                  {
                                                    style: "currency",
                                                    currency: "USD",
                                                  }
                                                ).format(inv.avcg)}`
                                              : "N/A"}
                                          </span>
                                        </Table.Cell>
                                        <Table.Cell
                                          style={{ padding: "0.3em" }}
                                        >
                                          <span
                                            style={{
                                              backgroundColor:
                                                inv.last_ninety_day_avg_cogs ===
                                                  lowest90DayAvg &&
                                                inv.last_ninety_day_avg_cogs !=
                                                  null
                                                  ? "#e6ffe6"
                                                  : "transparent",
                                              padding: "0.2rem 0.4rem",
                                              borderRadius: "2px",
                                            }}
                                          >
                                            {inv.last_ninety_day_avg_cogs !=
                                            null
                                              ? `${new Intl.NumberFormat(
                                                  "en-US",
                                                  {
                                                    style: "currency",
                                                    currency: "USD",
                                                  }
                                                ).format(
                                                  inv.last_ninety_day_avg_cogs
                                                )}`
                                              : "N/A"}
                                          </span>
                                        </Table.Cell>
                                        <Table.Cell
                                          style={{ padding: "0.3em" }}
                                        >
                                          {rowIndex === 0 &&
                                          supplyUnits &&
                                          supplyUnits.length > 0 ? (
                                            <div style={{ fontSize: "0.85em" }}>
                                              {supplyUnits.map(
                                                (unit, unitIndex) => (
                                                  <div
                                                    key={unitIndex}
                                                    style={{
                                                      marginBottom: "0.2rem",
                                                    }}
                                                  >
                                                    <div>
                                                      <strong>UPC:</strong>{" "}
                                                      {unit.upc || "N/A"}
                                                    </div>
                                                    <Label
                                                      color={getRestockStatusColor(
                                                        unit.restock_status_ftp
                                                      )}
                                                      size="tiny"
                                                    >
                                                      {getRestockStatusLabel(
                                                        unit.restock_status_ftp
                                                      )}
                                                    </Label>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          ) : rowIndex === 0 ? (
                                            <span
                                              style={{
                                                fontSize: "0.85em",
                                                color: "rgba(0,0,0,0.5)",
                                              }}
                                            >
                                              No supply units
                                            </span>
                                          ) : null}
                                        </Table.Cell>
                                      </Table.Row>
                                    )
                                  )}
                                </React.Fragment>
                              );
                            })}
                        </Table.Body>
                      </Table>
                    </div>
                  )}
                </Segment>
              );
            })}
          </div>
        )}
      </Modal.Content>
    </Modal>
  );
};
